import * as React from "react";
import { Box, Link, Typography } from "@mui/material";

export default function SubscriptionsDoc({ onNavigate }) {
  return (
    <Box id="subscriptions" sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Subscriptions
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Subscriptions represent billing relationships between organizations and plans.
        They track status, dates, and payment details.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Subscription types
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Subscriptions come from different sources (e.g. Squarespace, MoneyMinder).
        Cancellation behavior varies by type:
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 0.5 } }}>
        <li>
          <strong>Squarespace</strong> – The user can cancel when in{" "}
          <strong>pending</strong> or <strong>active</strong> state from the user
          portal. An admin can also cancel from the admin Subscription Detail page.
        </li>
        <li>
          <strong>Other types</strong> – Cancellation may be handled differently;
          check the plan source and vendor.
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Status values
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Subscriptions use one of four statuses: <strong>active</strong>,{" "}
        <strong>pending</strong>, <strong>cancelled</strong>, or{" "}
        <strong>expired</strong>. Only active or pending subscriptions can be
        cancelled.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Key fields
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 0.5 } }}>
        <li>
          <strong>Plan</strong> – Links to subscription_plans (e.g. SquareSpace Core,
          MoneyMinder)
        </li>
        <li>
          <strong>Organization</strong> – The org that owns the subscription
        </li>
        <li>
          <strong>Renewal date</strong> – When the subscription renews
        </li>
        <li>
          <strong>Cancelled date</strong> – Set when status becomes cancelled
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Cancelling a subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>From the user portal:</strong> For Squarespace subscriptions in
        pending or active state, org admins see a Cancel button on the
        Subscriptions tab. After confirming, the status is set to cancelled.
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>From the admin:</strong> On the Subscription Detail page, when
        status is <strong>active</strong>, a Cancel button appears in the Status
        panel (right column, above Metadata). Clicking it opens a confirmation
        dialog. After confirming, the status is set to cancelled and the
        cancelled date is recorded.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Testing subscriptions
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        To test the cancel flow: open a subscription with status &quot;active&quot;,
        use the Cancel button in the Status panel, confirm in the dialog, and
        verify the status updates to &quot;cancelled&quot; and the cancelled date is set.
      </Typography>

      {onNavigate && (
        <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/subscriptions")}
          >
            Go to Subscriptions list →
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              onNavigate("/dashboard/docs?topic=squarespace-website");
            }}
          >
            Squarespace website process →
          </Link>
        </Box>
      )}
    </Box>
  );
}
